import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Mascot } from "@/components/brand/Mascot";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { OrderStatus, OrderSummary } from "@/components/OrderSummary";
import { useCart } from "@/context/CartContext";
import { getCheckoutStatus, getOrder } from "@/lib/api";

const MAX_TRIES = 20;

export default function OrderSuccess() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sid) return setState("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const s = await getCheckoutStatus(sid);
        if (s.payment_status === "paid") {
          setOrder(await getOrder(id, sid));
          if (!cleared.current) { cleared.current = true; clear(); }
          return setState("paid");
        }
        if (s.status === "expired") return setState("expired");
      } catch { /* keep polling */ }
      if (tries >= MAX_TRIES) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [id, sid, clear]);

  return (
    <div className="mx-auto max-w-[1100px] px-5 pb-28 pt-14 sm:px-10 lg:pt-24" data-testid="order-success-page">
      <Seo title="Order confirmed" description="Your order with the suspects." path={`/order/${id}/success`} noindex />
      {state === "checking" && (
        <div className="flex flex-col items-start gap-6" data-testid="order-checking">
          <Mascot size={70} className="animate-pulse" label="" />
          <h1 className="display text-[clamp(3rem,7vw,5.5rem)]">Confirming payment…</h1>
          <p className="text-ink/70">Hang tight, we're checking with Stripe. This usually takes a few seconds.</p>
        </div>
      )}
      {state === "paid" && order && (
        <div className="grid gap-14 lg:grid-cols-12" data-testid="order-confirmed">
          <div className="lg:col-span-5">
            <div className="bg-lime p-8 sm:p-10">
              <MascotDoodle variant="crown" size={130} bg="#FF7EC7" />
              <p className="label mt-8 font-bold">Order number</p>
              <p className="display mt-2 text-5xl" data-testid="order-number">{order.order_number}</p>
            </div>
            <h1 className="display mt-10 text-[clamp(3.25rem,6vw,5.5rem)]">Certified <span className="hl hl-pink hl-thick">sus.</span><br />Order confirmed.</h1>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-ink/75">Thanks {order.name.split(" ")[0]}. A confirmation is on its way to <b>{order.email}</b>. We pack in Toronto and ship in 1 to 3 business days.</p>
            <div className="mt-6"><OrderStatus status={order.status} /></div>
            <Link to="/shop" className="btn btn-ink mt-10" data-testid="order-keep-shopping">Keep shopping <ArrowRight size={16} /></Link>
          </div>
          <div className="lg:col-span-6 lg:col-start-7"><OrderSummary order={order} /></div>
        </div>
      )}
      {["expired", "timeout", "error"].includes(state) && (
        <div className="flex flex-col items-start gap-6" data-testid={`order-${state}`}>
          <MascotDoodle variant="headphones" size={120} bg="#7FD3FF" />
          <h1 className="display text-[clamp(3rem,7vw,5.5rem)]">{state === "expired" ? "Payment session expired" : "We couldn't confirm that yet"}</h1>
          <p className="max-w-lg text-ink/70">{state === "expired" ? "Nothing was charged. Your cart is still saved." : "If you completed payment, your confirmation email will arrive shortly. You can also check with Order lookup."}</p>
          <div className="flex flex-wrap gap-6"><Link to="/checkout" className="btn btn-lime" data-testid="order-back-checkout">Back to checkout</Link><Link to="/order-lookup" className="link-u self-center" data-testid="order-lookup-link">Order lookup</Link></div>
        </div>
      )}
    </div>
  );
}
